import {
  mergeClasses,
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tab,
  TabList,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Delete16Filled,
  Delete16Regular,
  MoreHorizontal20Filled,
  MoreHorizontal20Regular,
  Prohibited16Filled,
  Prohibited16Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DatasetFileViewer } from '../../../components/Datalake/DatasetAnnotationsTab/AnnotationEditor/index.js';
import { DatasetAnnotationsTab } from '../../../components/Datalake/DatasetAnnotationsTab/DatasetAnnotationsTab.js';
import { DatasetAttributesTab } from '../../../components/Datalake/DatasetAttributesTab/DatasetAttributesTab.js';
import { DatasetEntriesTable } from '../../../components/Datalake/DatasetEntriesTable/DatasetEntriesTable.js';
import { DatasetOverviewTab } from '../../../components/Datalake/DatasetOverviewTab/DatasetOverviewTab.js';
import { DatasetPermissionsTab } from '../../../components/Datalake/DatasetPermissionsTab/DatasetPermissionsTab.js';
import { DatasetVersionsTab } from '../../../components/Datalake/DatasetVersionsTab/DatasetVersionsTab.js';
import { DeleteParticipantDataDialog } from '../../../components/Datalake/DeleteParticipantDataDialog/DeleteParticipantDataDialog.js';
import { EditDatasetDialog } from '../../../components/Datalake/EditDatasetDialog/EditDatasetDialog.js';
import type { DatasetCreateDataType } from '../../../constants/index.js';
import {
  DATASET_CREATE_DATA_TYPES,
  DATASET_EDIT_TOOLTIP_NOT_DRAFT,
  DATASET_ROLE_CONTRIBUTOR,
  DATASET_ROLE_OWNER,
  DATASET_ROLE_TOOLTIP_CONTRIBUTOR,
  DATASET_ROLE_TOOLTIP_OWNER,
} from '../../../constants/index.js';
import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { hasDatasetRole } from '../../../hooks/useMyDatasetRole/hasDatasetRole.js';
import { useMyDatasetRole } from '../../../hooks/useMyDatasetRole/useMyDatasetRole.js';
import { AccessControlService } from '../../../services/accessControlService.js';
import type {
  DatasetVersion,
  UpdateDatasetMetaRequest,
} from '../../../services/dataHubService.interface.js';
import { withCorrelationId } from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';

import type {
  DatasetDetailTab,
  DatasetDetailViewProps,
  VersionViewInfo,
} from './DatasetDetailView.interface.js';
import { useStyles } from './DatasetDetailView.style.js';
import { DatasetVersionDropdown } from './DatasetVersionDropdown.js';

const MoreIcon = bundleIcon(
  MoreHorizontal20Filled,
  MoreHorizontal20Regular,
) as React.FC;
const DeleteIcon = bundleIcon(Delete16Filled, Delete16Regular) as React.FC;
const HardDeleteIcon = bundleIcon(
  Prohibited16Filled,
  Prohibited16Regular,
) as React.FC;
const VIEWER_EMPTY_MESSAGE =
  'Open a file with the maximize button from the Data tab to view it here.';

/**
 * Tabs whose content is governed by the annotation-set lifecycle, not the
 * dataset's. The dataset-level version/publish/delete controls are hidden on
 * these tabs to avoid conflating the two independent versioning concepts.
 */
const TABS_WITHOUT_DATASET_ACTIONS: ReadonlySet<DatasetDetailTab> = new Set([
  'annotations',
  'viewer',
]);

export function DatasetDetailView({
  dataset,
  activeTab,
  onTabChange,
  service,
  onDeleted,
  onRefetch,
  onAnnotateEntry,
  annotateTarget,
  viewerTarget,
  onOpenViewer,
  annotationContent,
}: DatasetDetailViewProps) {
  const styles = useStyles();
  const client = useDataHubClient();
  const scenarioFactory = useScenarioFactory();
  const acService = useMemo(() => new AccessControlService(client), [client]);
  const { roles: myRoles } = useMyDatasetRole({
    dataSetId: dataset.id,
    service: acService,
  });
  const canContribute = hasDatasetRole(myRoles, DATASET_ROLE_CONTRIBUTOR);
  const canOwn = hasDatasetRole(myRoles, DATASET_ROLE_OWNER);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hardDeleteDialogOpen, setHardDeleteDialogOpen] = useState(false);
  const [draftDeleteDialogOpen, setDraftDeleteDialogOpen] = useState(false);
  const [participantDialogOpen, setParticipantDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [dataEntryCount, setDataEntryCount] = useState<number | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [versionView, setVersionView] = useState<VersionViewInfo | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const canEditMeta = canContribute && dataset.status === 'Draft';
  const editDisabledReason = !canContribute
    ? DATASET_ROLE_TOOLTIP_CONTRIBUTOR
    : DATASET_EDIT_TOOLTIP_NOT_DRAFT;

  const handleUpdateMeta = useCallback(
    async (request: UpdateDatasetMetaRequest) => {
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeUpdateDataset,
      );
      try {
        const result = await service.updateDatasetMeta(dataset.id, request, {
          extras: withCorrelationId(scenario.queryId),
        });
        scenario.complete('success');
        return result;
      } catch (error) {
        const classification = classifyScenarioError(error);
        scenario.complete(classification.status, classification.data);
        throw error;
      }
    },
    [service, dataset.id, scenarioFactory],
  );

  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    setPublishError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakePublishDataset,
    );
    try {
      await service.publishDataset(dataset.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success');
      onRefetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setPublishError(
        err instanceof Error ? err.message : 'Failed to publish dataset',
      );
    } finally {
      setIsPublishing(false);
    }
  }, [service, dataset.id, onRefetch, scenarioFactory]);

  const handleDemote = useCallback(async () => {
    setIsPublishing(true);
    setPublishError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDemoteDataset,
    );
    try {
      await service.createDraftDataset(dataset.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success');
      onRefetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setPublishError(
        err instanceof Error ? err.message : 'Failed to demote dataset',
      );
    } finally {
      setIsPublishing(false);
    }
  }, [service, dataset.id, onRefetch, scenarioFactory]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteDataset,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'soft' };
    try {
      await service.deleteDataset(dataset.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success', scenarioData);
      onDeleted();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete dataset',
      );
      setIsDeleting(false);
    }
  }, [service, dataset.id, onDeleted, scenarioFactory]);

  const handleHardDelete = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteDataset,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'hard' };
    try {
      await service.hardDeleteDataset(dataset.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success', scenarioData);
      onDeleted();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete dataset',
      );
      setIsDeleting(false);
    }
  }, [service, dataset.id, onDeleted, scenarioFactory]);

  const handleDeleteDraft = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteDataset,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'draft' };
    try {
      await service.deleteDatasetDraft(dataset.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success', scenarioData);
      setDraftDeleteDialogOpen(false);
      setIsDeleting(false);
      onRefetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete draft version',
      );
      setIsDeleting(false);
    }
  }, [service, dataset.id, onRefetch, scenarioFactory]);

  const handleTabChange = useCallback(
    (tab: DatasetDetailTab) => {
      setVersionView(null);
      onTabChange(tab);
    },
    [onTabChange],
  );

  const handleViewVersionData = useCallback(
    (version: DatasetVersion) => {
      setVersionView({
        version: version.version,
        status: version.status,
        createdAt: version.createdAt,
      });
      onTabChange('data-items');
    },
    [onTabChange],
  );

  const dataVersion = versionView?.version ?? dataset.version;
  const isViewingLatest = versionView === null;

  useEffect(() => {
    if (activeTab !== 'overview') return;

    let cancelled = false;
    setDataEntryCount(null);

    service
      .listDataEntries({
        dataSetId: dataset.id,
        dataSetVersion: dataset.version,
        pageSize: 1,
        includeCount: true,
      })
      .then((result) => {
        if (!cancelled) setDataEntryCount(result.totalCount);
      })
      .catch(() => {
        if (!cancelled) setDataEntryCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, dataset.id, dataset.version, service]);

  const showDatasetActions = !TABS_WITHOUT_DATASET_ACTIONS.has(activeTab);

  return (
    <div className={styles.root}>
      {/* Header with actions */}
      <div className={styles.header}>
        <Text as="span" className={styles.headerTitle}>
          {dataset.name}
        </Text>

        <div className={styles.headerActions}>
          {showDatasetActions && (
            <>
              <DatasetVersionDropdown
                datasetId={dataset.id}
                latestVersion={dataset.version}
                latestStatus={dataset.status}
                versionView={versionView}
                onVersionChange={(view) => {
                  setVersionView(view);
                  if (view !== null) {
                    onTabChange('data-items');
                  }
                }}
              />
              {dataset.status === 'Draft' &&
                isViewingLatest &&
                (canContribute ? (
                  <Button
                    appearance="primary"
                    size="small"
                    disabled={isPublishing}
                    icon={isPublishing ? <Spinner size="tiny" /> : undefined}
                    onClick={() => {
                      void handlePublish();
                    }}
                  >
                    Publish
                  </Button>
                ) : (
                  <Tooltip
                    content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                    relationship="description"
                  >
                    <Button appearance="primary" size="small" disabledFocusable>
                      Publish
                    </Button>
                  </Tooltip>
                ))}
              {dataset.status === 'Published' &&
                isViewingLatest &&
                (canContribute ? (
                  <Button
                    appearance="secondary"
                    size="small"
                    disabled={isPublishing}
                    icon={isPublishing ? <Spinner size="tiny" /> : undefined}
                    onClick={() => {
                      void handleDemote();
                    }}
                  >
                    Demote to draft
                  </Button>
                ) : (
                  <Tooltip
                    content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                    relationship="description"
                  >
                    <Button
                      appearance="secondary"
                      size="small"
                      disabledFocusable
                    >
                      Demote to draft
                    </Button>
                  </Tooltip>
                ))}
              {publishError !== null && (
                <Text className={styles.actionError}>{publishError}</Text>
              )}

              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<MoreIcon />}
                    aria-label="More actions"
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {dataset.status === 'Draft' &&
                      (canContribute ? (
                        <MenuItem
                          data-testid="delete-draft-menu-item"
                          icon={<DeleteIcon />}
                          onClick={() => {
                            setDeleteError(null);
                            setDraftDeleteDialogOpen(true);
                          }}
                        >
                          Delete Draft
                        </MenuItem>
                      ) : (
                        <Tooltip
                          content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                          relationship="description"
                        >
                          <MenuItem
                            data-testid="delete-draft-menu-item"
                            icon={<DeleteIcon />}
                            disabled
                          >
                            Delete Draft
                          </MenuItem>
                        </Tooltip>
                      ))}
                    {canContribute ? (
                      <MenuItem
                        data-testid="delete-menu-item"
                        icon={<DeleteIcon />}
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </MenuItem>
                    ) : (
                      <Tooltip
                        content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                        relationship="description"
                      >
                        <MenuItem
                          data-testid="delete-menu-item"
                          icon={<DeleteIcon />}
                          disabled
                        >
                          Delete
                        </MenuItem>
                      </Tooltip>
                    )}
                    {canOwn ? (
                      <MenuItem
                        data-testid="hard-delete-menu-item"
                        icon={<HardDeleteIcon />}
                        className={styles.hardDeleteItem}
                        onClick={() => {
                          setDeleteError(null);
                          setHardDeleteDialogOpen(true);
                        }}
                      >
                        Hard Delete
                      </MenuItem>
                    ) : (
                      <Tooltip
                        content={DATASET_ROLE_TOOLTIP_OWNER}
                        relationship="description"
                      >
                        <MenuItem
                          data-testid="hard-delete-menu-item"
                          icon={<HardDeleteIcon />}
                          className={styles.hardDeleteItem}
                          disabled
                        >
                          Hard Delete
                        </MenuItem>
                      </Tooltip>
                    )}
                    {canOwn ? (
                      <MenuItem
                        data-testid="delete-participant-menu-item"
                        icon={<HardDeleteIcon />}
                        className={styles.hardDeleteItem}
                        onClick={() => {
                          setParticipantDialogOpen(true);
                        }}
                      >
                        Delete participant data
                      </MenuItem>
                    ) : (
                      <Tooltip
                        content={DATASET_ROLE_TOOLTIP_OWNER}
                        relationship="description"
                      >
                        <MenuItem
                          data-testid="delete-participant-menu-item"
                          icon={<HardDeleteIcon />}
                          className={styles.hardDeleteItem}
                          disabled
                        >
                          Delete participant data
                        </MenuItem>
                      </Tooltip>
                    )}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </>
          )}
        </div>
      </div>

      {/* Soft-delete confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(_, data) => {
          setDeleteDialogOpen(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete dataset?</DialogTitle>
            <DialogContent>
              <Body1>
                &ldquo;{dataset.name}&rdquo; will be soft-deleted and can be
                restored later.
              </Body1>
              {deleteError !== null && (
                <Body1 className={styles.deleteError}>{deleteError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isDeleting}>
                  Cancel
                </Button>
              </DialogTrigger>
              <Button
                data-testid="delete-confirm-button"
                appearance="primary"
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                disabled={isDeleting}
                onClick={() => {
                  void handleDelete();
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Hard-delete confirmation */}
      <Dialog
        open={hardDeleteDialogOpen}
        onOpenChange={(_, data) => {
          setHardDeleteDialogOpen(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Permanently delete dataset?</DialogTitle>
            <DialogContent>
              <Body1>
                &ldquo;{dataset.name}&rdquo; and all its data will be
                permanently deleted. This action cannot be undone.
              </Body1>
              {deleteError !== null && (
                <Body1 className={styles.deleteError}>{deleteError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isDeleting}>
                  Cancel
                </Button>
              </DialogTrigger>
              <Button
                data-testid="hard-delete-confirm-button"
                appearance="primary"
                className={styles.hardDeleteConfirmButton}
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                disabled={isDeleting}
                onClick={() => {
                  void handleHardDelete();
                }}
              >
                Permanently Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Draft-delete confirmation */}
      <Dialog
        open={draftDeleteDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open && !isDeleting) setDraftDeleteDialogOpen(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete draft version?</DialogTitle>
            <DialogContent>
              <Body1>
                The draft version of &ldquo;{dataset.name}&rdquo; will be
                permanently deleted, discarding unpublished changes. If this is
                the only version, the entire dataset will be deleted.
              </Body1>
              {deleteError !== null && (
                <Body1 className={styles.deleteError}>{deleteError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isDeleting}>
                  Cancel
                </Button>
              </DialogTrigger>
              <Button
                data-testid="delete-draft-confirm-button"
                appearance="primary"
                className={styles.hardDeleteConfirmButton}
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                disabled={isDeleting}
                onClick={() => {
                  void handleDeleteDraft();
                }}
              >
                Delete Draft
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete-participant-data dialog */}
      <DeleteParticipantDataDialog
        open={participantDialogOpen}
        dataSetId={dataset.id}
        datasetName={dataset.name}
        service={service}
        onClose={() => setParticipantDialogOpen(false)}
        onDeleted={onRefetch}
      />

      {/* Edit dataset metadata dialog */}
      <EditDatasetDialog
        open={editDialogOpen}
        dataset={dataset}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleUpdateMeta}
        onSaved={() => onRefetch()}
      />

      {/* Tab bar */}
      <div className={styles.tabBar}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data) => {
            handleTabChange(data.value as DatasetDetailTab);
          }}
        >
          <Tab value="overview">Overview</Tab>
          <Tab value="versions">Versions</Tab>
          <Tab value="data-items">Data</Tab>
          <Tab value="permissions">Permissions</Tab>
          <Tab value="attributes">Attributes</Tab>
          <Tab value="annotations">Annotations</Tab>
          <Tab value="viewer">Viewer</Tab>
        </TabList>
      </div>

      {/* Tab content */}
      <div
        className={mergeClasses(
          styles.tabContent,
          activeTab === 'annotations' &&
            annotationContent != null &&
            styles.routedContent,
        )}
      >
        {activeTab === 'data-items' && (
          <>
            <DatasetEntriesTable
              dataSetId={dataset.id}
              dataSetVersion={dataVersion}
              datasetName={dataset.name}
              datasetStatus={dataset.status}
              dataType={
                DATASET_CREATE_DATA_TYPES.includes(
                  dataset.dataType as DatasetCreateDataType,
                )
                  ? (dataset.dataType as DatasetCreateDataType)
                  : undefined
              }
              readOnly={versionView !== null}
              onStatusChanged={() => onRefetch()}
              onAnnotateEntry={onAnnotateEntry}
              onOpenViewer={onOpenViewer}
              myRoles={myRoles}
            />
          </>
        )}
        {activeTab === 'overview' && (
          <DatasetOverviewTab
            dataset={dataset}
            dataEntryCount={dataEntryCount}
            canEdit={canEditMeta}
            editDisabledReason={editDisabledReason}
            onEditClick={() => setEditDialogOpen(true)}
          />
        )}
        {activeTab === 'versions' && (
          <DatasetVersionsTab
            dataSetId={dataset.id}
            onViewVersionData={handleViewVersionData}
          />
        )}
        {activeTab === 'permissions' && (
          <DatasetPermissionsTab dataSetId={dataset.id} myRoles={myRoles} />
        )}
        {activeTab === 'attributes' && (
          <DatasetAttributesTab dataSetId={dataset.id} myRoles={myRoles} />
        )}
        {activeTab === 'annotations' && (
          <DatasetAnnotationsTab
            annotationContent={annotationContent}
            dataSetId={dataset.id}
            datasetName={dataset.name}
            dataSetVersion={dataVersion}
            dataSetStatus={dataset.status}
            myRoles={myRoles}
            scopedDataId={annotateTarget?.dataId}
            scopedFileId={annotateTarget?.fileId}
          />
        )}
        {activeTab === 'viewer' &&
          (viewerTarget !== undefined ? (
            <div className={styles.viewerFill}>
              <DatasetFileViewer
                datasetId={dataset.id}
                datasetName={dataset.name}
                dataId={viewerTarget.dataId}
                datasetVersion={dataVersion}
                fileId={viewerTarget.fileId}
              />
            </div>
          ) : (
            <MessageBar intent="info" role="status">
              <MessageBarBody>{VIEWER_EMPTY_MESSAGE}</MessageBarBody>
            </MessageBar>
          ))}
      </div>
    </div>
  );
}
